/**
 * MarkdownView (1.34.0) — минималистичный рендер markdown для юридических
 * документов Кворум.
 *
 * Поддерживает ровно то, что используется в legal_documents:
 *   • # H1, ## H2, ### H3
 *   • **жирный** в инлайн-тексте
 *   • маркированные списки (строка начинается с "- ")
 *   • нумерованные списки (строка начинается с "1. ", "2. " и т.д.)
 *   • абзацы (разделены пустой строкой)
 *
 * Не поддерживает: код, таблицы, цитаты, ссылки, картинки, HTML. Если
 * понадобится — добавим точечно, иначе тащить react-markdown ради
 * нескольких документов — избыточно.
 *
 * Безопасность: HTML в markdown НЕ рендерится, всё содержимое выводится
 * как обычный текст React-нодами. XSS невозможен.
 */

import { type ReactNode } from 'react';
import { C, F } from '../tokens';

interface MarkdownViewProps {
  source: string;
}

export function MarkdownView({ source }: MarkdownViewProps) {
  const blocks = parseBlocks(source);
  return (
    <div style={{
      color: C.txt,
      fontFamily: F.body,
      fontSize: 14,
      lineHeight: 1.6,
      maxWidth: 720,
    }}>
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

type Block =
  | { type: 'h1' | 'h2' | 'h3'; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul' | 'ol'; items: string[] };

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Пустая строка — пропускаем
    if (trimmed === '') {
      i++;
      continue;
    }

    // Заголовки
    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'h3', text: trimmed.slice(4) });
      i++;
      continue;
    }
    if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'h2', text: trimmed.slice(3) });
      i++;
      continue;
    }
    if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'h1', text: trimmed.slice(2) });
      i++;
      continue;
    }

    // Маркированный список
    if (trimmed.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // Нумерованный список
    if (/^\d+\.\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // Обычный абзац — собираем последовательные не-пустые строки
    const lines_p: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !isBlockStart(lines[i])) {
      lines_p.push(lines[i].trim());
      i++;
    }
    if (lines_p.length > 0) {
      blocks.push({ type: 'p', text: lines_p.join(' ') });
    }
  }

  return blocks;
}

function isBlockStart(line: string): boolean {
  const t = line.trim();
  return t.startsWith('#') || t.startsWith('- ') || /^\d+\.\s/.test(t);
}

function renderBlock(b: Block, key: number): ReactNode {
  switch (b.type) {
    case 'h1':
      return (
        <h1 key={key} style={{
          fontFamily: F.head,
          fontWeight: 800,
          fontSize: 24,
          color: C.txt,
          margin: '24px 0 16px',
          lineHeight: 1.25,
        }}>
          {renderInline(b.text)}
        </h1>
      );
    case 'h2':
      return (
        <h2 key={key} style={{
          fontFamily: F.head,
          fontWeight: 700,
          fontSize: 18,
          color: C.txt,
          margin: '24px 0 10px',
          lineHeight: 1.3,
        }}>
          {renderInline(b.text)}
        </h2>
      );
    case 'h3':
      return (
        <h3 key={key} style={{
          fontFamily: F.head,
          fontWeight: 700,
          fontSize: 15,
          color: C.txt,
          margin: '18px 0 8px',
          lineHeight: 1.3,
        }}>
          {renderInline(b.text)}
        </h3>
      );
    case 'p':
      return (
        <p key={key} style={{ margin: '0 0 14px' }}>
          {renderInline(b.text)}
        </p>
      );
    case 'ul':
      return (
        <ul key={key} style={{ margin: '0 0 14px', paddingLeft: 22 }}>
          {b.items.map((item, i) => (
            <li key={i} style={{ margin: '0 0 6px' }}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key} style={{ margin: '0 0 14px', paddingLeft: 22 }}>
          {b.items.map((item, i) => (
            <li key={i} style={{ margin: '0 0 6px' }}>{renderInline(item)}</li>
          ))}
        </ol>
      );
  }
}

/**
 * Инлайн-разметка: только **жирный**. Возвращает массив ReactNode.
 */
function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }
    parts.push(<strong key={key++} style={{ fontWeight: 700, color: C.txt }}>{m[1]}</strong>);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}
